import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import { mangaList, mangaCategories, faqList } from '@/mocks/manga';

function MangaCard({ manga, index }: { manga: typeof mangaList[0]; index: number }) {
  const [imageLoaded, setImageLoaded] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setTimeout(() => {
              entry.target.classList.add('opacity-100', 'translate-y-0');
              entry.target.classList.remove('opacity-0', 'translate-y-8');
            }, index * 80);
            observer.unobserve(entry.target);
          }
        });
      },
      { threshold: 0.1 }
    );

    if (cardRef.current) {
      observer.observe(cardRef.current);
    }

    return () => observer.disconnect();
  }, [index]);

  return (
    <div
      ref={cardRef}
      className="opacity-0 translate-y-8 transition-all duration-600 ease-out"
    >
      <Link
        to={`/article/${manga.id}`}
        className="group block bg-background-50 rounded-lg border border-background-200/70 overflow-hidden hover:border-primary-200 transition-all duration-300 cursor-pointer"
      >
        <div className="relative aspect-[3/4] overflow-hidden">
          <img
            src={manga.cover}
            alt={manga.title}
            className={`w-full h-full object-cover object-top group-hover:scale-105 transition-transform duration-500 ${
              imageLoaded ? 'opacity-100' : 'opacity-0'
            }`}
            onLoad={() => setImageLoaded(true)}
          />
          {!imageLoaded && (
            <div className="absolute inset-0 bg-background-200 animate-pulse" />
          )}
          <div className="absolute top-2 left-2 px-2 py-0.5 rounded-md bg-primary-500/90 text-background-50 text-xs font-medium">
            {manga.rating}
          </div>
          <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent pt-8 pb-2 px-3">
            <span className="text-background-50 text-xs font-medium">{manga.status}</span>
          </div>
        </div>
        <div className="p-3">
          <h3 className="text-sm font-heading font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors line-clamp-1">
            {manga.title}
          </h3>
          <p className="text-xs text-foreground-500 mt-1">{manga.author}</p>
          <div className="flex items-center gap-2 mt-2">
            <span className="text-xs text-foreground-400">{manga.chapters}话</span>
            <span className="text-xs text-foreground-300">·</span>
            <span className="text-xs text-foreground-400">{(manga.views / 10000).toFixed(0)}万人气</span>
          </div>
        </div>
      </Link>
    </div>
  );
}

function HomeSEO() {
  const schema = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'Organization',
        name: '樱花动漫之家',
        url: 'https://sakuranga.com/',
        description: '樱花动漫之家 — 聚合热门日本漫画资讯的日漫推荐与排行平台',
        contactPoint: {
          '@type': 'ContactPoint',
          email: 'contact@sakuranga.com',
          contactType: 'customer service',
        },
      },
      {
        '@type': 'WebSite',
        name: '樱花动漫之家',
        url: 'https://sakuranga.com/',
        potentialAction: {
          '@type': 'SearchAction',
          target: 'https://sakuranga.com/search?q={search_term_string}',
          'query-input': 'required name=search_term_string',
        },
      },
      {
        '@type': 'WebPage',
        name: '樱花动漫之家 — 日漫推荐与排行连载',
        description: '樱花动漫之家的日漫专区为你聚合热门日本漫画，涵盖日漫推荐与排行。高质量日本漫画资源一网打尽，从经典到最新连载，满足你的日漫追更和探索需求。',
        url: 'https://sakuranga.com/',
        isPartOf: { '@id': 'https://sakuranga.com/#website' },
        about: { '@id': 'https://sakuranga.com/#organization' },
      },
      {
        '@type': 'FAQPage',
        mainEntity: faqList.map((faq) => ({
          '@type': 'Question',
          name: faq.question,
          acceptedAnswer: {
            '@type': 'Answer',
            text: faq.answer,
          },
        })),
      },
    ],
  };

  return (
    <script
      type="application/ld+json"
      dangerouslySetInnerHTML={{ __html: JSON.stringify(schema) }}
    />
  );
}

function SearchBar() {
  const [query, setQuery] = useState('');

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    if (query.trim()) {
      const searchUrl = `/category/shonen?search=${encodeURIComponent(query.trim())}`;
      window.REACT_APP_NAVIGATE(searchUrl);
    }
  };

  return (
    <form onSubmit={handleSearch} className="flex items-center gap-0 w-full max-w-lg">
      <div className="flex-1 relative">
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="搜索你喜欢的日本漫画..."
          className="w-full h-11 pl-4 pr-10 rounded-l-full bg-background-50/95 border border-background-200/70 text-sm text-foreground-950 placeholder-foreground-400 focus:outline-none focus:border-primary-300 transition-colors"
        />
      </div>
      <button
        type="submit"
        className="h-11 px-6 rounded-r-full bg-primary-500 text-background-50 text-sm font-medium hover:bg-primary-600 transition-colors cursor-pointer whitespace-nowrap flex items-center gap-1.5"
      >
        <div className="w-4 h-4 flex items-center justify-center">
          <i className="ri-search-line text-sm"></i>
        </div>
        搜索
      </button>
    </form>
  );
}

export default function Home() {
  const topRanked = [...mangaList].sort((a, b) => b.rating - a.rating).slice(0, 8);
  const latestUpdated = [...mangaList].sort((a, b) => new Date(b.updateDate).getTime() - new Date(a.updateDate).getTime()).slice(0, 8);

  return (
    <Layout>
      <HomeSEO />

      {/* Hero */}
      <section className="relative w-full min-h-[500px] md:min-h-[620px] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Japanese%20anime%20style%20art%20background%20with%20soft%20sakura%20cherry%20blossom%20petals%20floating%20in%20the%20air%2C%20warm%20golden%20sunset%20lighting%2C%20traditional%20Japanese%20elements%20like%20torii%20gate%20silhouette%2C%20manga%20ink%20texture%20subtle%20overlay%2C%20dreamy%20atmospheric%20illustration%2C%20warm%20pink%20and%20cream%20tones%2C%20no%20text%2C%20abstract%20and%20artistic%20style&width=1600&height=900&seq=hero-bg-01&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-center"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-b from-black/45 via-black/30 to-black/50" />
        <div className="relative z-10 w-full px-4 md:px-6 py-16 md:py-20 text-center">
          <div className="max-w-3xl mx-auto">
            <div className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-background-50/20 backdrop-blur-sm border border-background-50/30 mb-6">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-sparkling-line text-background-50 text-xs"></i>
              </div>
              <span className="text-background-50 text-xs font-medium">日漫爱好者的聚集地</span>
            </div>
            <h1 className="text-3xl md:text-5xl lg:text-6xl font-heading font-bold text-background-50 leading-tight">
              发现你最爱的
              <span className="block mt-2 text-primary-300">日本漫画</span>
            </h1>
            <p className="mt-4 md:mt-5 text-sm md:text-base text-background-50/80 max-w-xl mx-auto leading-relaxed">
              从经典名作到最新连载，热门日漫推荐与排行一网打尽，开启你的日漫探索之旅
            </p>
            <div className="mt-7 md:mt-8 flex justify-center">
              <SearchBar />
            </div>
            <div className="flex flex-wrap items-center justify-center gap-6 md:gap-10 mt-8 md:mt-10">
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-heading font-bold text-background-50">1000+</div>
                <div className="text-xs text-background-50/70 mt-1">日漫资源</div>
              </div>
              <div className="w-px h-8 bg-background-50/20 hidden sm:block" />
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-heading font-bold text-background-50">8</div>
                <div className="text-xs text-background-50/70 mt-1">精选分类</div>
              </div>
              <div className="w-px h-8 bg-background-50/20 hidden sm:block" />
              <div className="text-center">
                <div className="text-2xl md:text-3xl font-heading font-bold text-background-50">100万+</div>
                <div className="text-xs text-background-50/70 mt-1">日漫爱好者</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Hot Rankings */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-end justify-between mb-8 md:mb-10">
            <div>
              <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-950">
                热门推荐
                <span className="ml-2 inline-block w-6 h-0.5 bg-primary-500 align-middle mb-1.5"></span>
              </h2>
              <p className="text-sm text-foreground-500 mt-2">最受日漫爱好者欢迎的高分漫画</p>
            </div>
            <Link
              to="/category/shonen"
              className="hidden sm:flex items-center gap-1 text-sm text-primary-500 hover:text-primary-600 font-medium transition-colors cursor-pointer whitespace-nowrap"
            >
              查看全部
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-arrow-right-line text-sm"></i>
              </div>
            </Link>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {topRanked.map((manga, i) => (
              <MangaCard key={manga.id} manga={manga} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* Categories */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-10">
            <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-950">
              分类浏览
              <span className="ml-2 inline-block w-6 h-0.5 bg-accent-500 align-middle mb-1.5"></span>
            </h2>
            <p className="text-sm text-foreground-500 mt-2">按你喜欢的类型探索日漫世界</p>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-4">
            {mangaCategories.map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="group flex flex-col items-center gap-3 p-5 md:p-6 rounded-lg bg-background-50 border border-background-200/70 hover:border-primary-200 hover:bg-primary-50/30 transition-all duration-300 cursor-pointer"
              >
                <div className="w-12 h-12 md:w-14 md:h-14 flex items-center justify-center rounded-full bg-accent-100 text-accent-600 group-hover:bg-primary-100 group-hover:text-primary-500 transition-colors">
                  <i className={`${cat.icon} text-xl md:text-2xl`}></i>
                </div>
                <div className="text-center">
                  <h3 className="text-sm md:text-base font-heading font-semibold text-foreground-950 group-hover:text-primary-500 transition-colors">
                    {cat.name}
                  </h3>
                  <p className="text-xs text-foreground-500 mt-1">{cat.description}</p>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* Latest Updates */}
      <section className="py-14 md:py-20 bg-background-50">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="flex items-end justify-between mb-8 md:mb-10">
            <div>
              <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-950">
                最新连载
                <span className="ml-2 inline-block w-6 h-0.5 bg-accent-500 align-middle mb-1.5"></span>
              </h2>
              <p className="text-sm text-foreground-500 mt-2">日漫最新更新，不错过任何精彩</p>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-5">
            {latestUpdated.map((manga, i) => (
              <MangaCard key={manga.id} manga={manga} index={i} />
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-14 md:py-20 bg-background-100">
        <div className="max-w-3xl mx-auto px-4 md:px-6">
          <div className="text-center mb-8 md:mb-10">
            <h2 className="text-xl md:text-3xl font-heading font-bold text-foreground-950">
              常见问题
            </h2>
            <p className="text-sm text-foreground-500 mt-2">关于樱花动漫之家，你可能想了解的</p>
          </div>
          <div className="space-y-3">
            {faqList.map((faq, i) => (
              <FAQItem key={i} faq={faq} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-16 md:py-24 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=Japanese%20anime%20style%20wide%20banner%20background%20with%20flowing%20sakura%20petals%20and%20soft%20gradient%20warm%20lighting%2C%20dreamy%20atmospheric%20illustration%2C%20manga%20panel%20grid%20subtle%20pattern%20overlay%2C%20warm%20pink%20and%20cream%20tones%2C%20abstract%20artistic%20style%2C%20no%20text%2C%20minimalist&width=1600&height=600&seq=cta-bg-01&orientation=landscape"
            alt=""
            className="w-full h-full object-cover object-center"
          />
        </div>
        <div className="absolute inset-0 bg-gradient-to-r from-primary-600/85 to-primary-800/85" />
        <div className="relative z-10 max-w-2xl mx-auto px-4 md:px-6 text-center">
          <h2 className="text-2xl md:text-4xl font-heading font-bold text-background-50">
            开启你的日漫之旅
          </h2>
          <p className="mt-3 text-sm md:text-base text-background-50/80">
            加入樱花动漫之家，发现更多好看的日本漫画
          </p>
          <Link
            to="/category/shonen"
            className="mt-6 inline-flex items-center gap-2 px-8 py-3 rounded-full bg-background-50 text-primary-600 text-sm font-semibold hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap"
          >
            立即探索
            <div className="w-4 h-4 flex items-center justify-center">
              <i className="ri-arrow-right-line text-sm"></i>
            </div>
          </Link>
        </div>
      </section>
    </Layout>
  );
}

function FAQItem({ faq }: { faq: typeof faqList[0] }) {
  const [open, setOpen] = useState(false);

  return (
    <div className="rounded-lg border border-background-200/70 bg-background-50 overflow-hidden">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between px-5 py-4 text-left cursor-pointer"
      >
        <span className="text-sm font-medium text-foreground-950 pr-4">{faq.question}</span>
        <div className={`w-5 h-5 flex items-center justify-center flex-shrink-0 transition-transform duration-300 ${open ? 'rotate-180' : ''}`}>
          <i className="ri-arrow-down-s-line text-foreground-500"></i>
        </div>
      </button>
      <div
        className={`overflow-hidden transition-all duration-300 ${
          open ? 'max-h-96 opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="px-5 pb-4">
          <p className="text-sm text-foreground-600 leading-relaxed">{faq.answer}</p>
        </div>
      </div>
    </div>
  );
}